"use client";

import React from "react";
import { Linkedin, Instagram } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const founders = [{
  id: 1,
  name: "Haron Hasni",
  title: "Founder Aizalea & AizaHcare",
  bio: "Haron Hasni is a technology and healthcare innovator with 27 years of expertise in data, AI and digital transformation and over two decades in healthcare. He has built and scaled multiple ventures while pioneering AI-powered healthcare solutions and intelligent medical infrastructure.",
  image: "https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/IMG_6529-scaled.png",
  linkedin: "https://linkedin.com/in/haron-hasni",
  instagram: "https://instagram.com"
}, {
  id: 2,
  name: "Yoro Diallo\xA0",
  title: "Strategic Board Advisor – Finance & Regulatory Affairs",
  bio: "A California-educated finance expert, Mr. Diallo leverages three decades of banking expertise to bridge international trade and investment between Africa and the Middle East. He has been the primary architect of Planet One Group’s African expansion, supported by his deep-rooted relationships with governments and financial institutions worldwide.",
  image: "https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/PHOTO-2026-03-26-11-59-54.jpeg",
  linkedin: "https://linkedin.com/in/yoro-diallo",
  instagram: "https://instagram.com"
}, {
  id: 3,
  name: "Jerome Chabin",
  title: "Strategic Board Advisor – Investment & Growth",
  bio: "Jérôme Chabin is a seasoned entrepreneur and co-founder of IAD a French Tech unicorn and one of France's leading platforms for digital innovation. He has a long-standing track record at the intersection of technology, digital infrastructure, and global policy. His work has consistently focused on building resilient, forward-looking ecosystems that protect and empower digital communities.",
  image: "https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/PHOTO-2026-03-26-11-54-46.jpeg",
  linkedin: "https://linkedin.com/in/jerome-chabin",
  instagram: "https://instagram.com"
}, {
  id: 4,
  name: "Calim Bouhadra",
  title: "Strategic Advisor – Partnerships & Strategic Relations",
  bio: "Calim Bouhadra is a distinguished executive whose career spans the worlds of motor racing and Swiss luxury watchmaking — two industries where precision, performance, and brand heritage are paramount. With deep experience in managing global partnerships and developing high-end, innovation-driven ventures, Calim brings a rare blend of operational excellence and brand intelligence.",
  image: "https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/PHOTO-2026-03-26-11-54-46-1.jpeg",
  linkedin: "https://linkedin.com/in/calim-bouhadra-86b5ab4",
  instagram: "https://instagram.com"
}, {
  id: 5,
  name: "Ahmad Salem",
  title: "Technology & Innovation Advisor – HealthTech Infrastructure",
  bio: "Executive business, entrepreneurial and inspirational leader with 20+ years of experience in healthcare, oil & gas and industrial sectors. Extensive experience in business management, people and talent development, digital transformation, consultancy, sales, accounts and business development.",
  image: "https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/PHOTO-2026-03-26-11-54-46-2.jpeg",
  linkedin: "https://linkedin.com/in/ahmad-salem",
  instagram: "https://instagram.com"
}, {
  id: 6,
  name: "Bernard Pujol",
  title: "Chief Operating Officer (COO) – Healthcare Operations",
  bio: "Bernard Pujol is a senior international executive with over 40 years of experience leading large-scale transformations, global infrastructures, and complex operations. With broad experience in e-government and PPP initiatives, he operates in a transversal leadership role across strategy, governance, and operational performance across sectors including technology, biotechnology, diagnostics, services, and startups.",
  image: "https://positive-cattle.10web.cloud/wp-content/uploads/2026/03/PHOTO-2026-03-26-12-44-17.jpeg",
  linkedin: "https://linkedin.com/in/bernard-pujol",
  instagram: "https://instagram.com"
}];
function FounderCard({
  founder,
  index
}: {
  founder: typeof founders[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
    transitionDelay: `${index * 200}ms`
  }}>
      <Card className="h-full border-none shadow-2xl shadow-primary/5 bg-card overflow-hidden group rounded-[2rem]">
        <CardContent className="p-8 md:p-10 flex flex-col h-full">
          <div className="w-full aspect-square mb-10 overflow-hidden rounded-3xl bg-muted relative">
            <div className="absolute inset-0 bg-primary/5 mix-blend-multiply z-10 transition-opacity duration-500 group-hover:opacity-0" />
            <img src={founder.image} alt={founder.name} className="w-full h-full object-cover transition-transform duration-700 ease-in-out group-hover:scale-105" />
          </div>
          
          <h3 className="text-3xl md:text-4xl font-bold text-primary mb-3 tracking-tight">
            {founder.name}
          </h3>
          
          <p className="text-lg md:text-xl font-medium text-secondary mb-6">
            {founder.title}
          </p>
          
          <p className="text-foreground/80 text-base md:text-lg leading-relaxed mb-10 flex-grow">
            {founder.bio}
          </p>
          
          <div className="flex items-center gap-4 mt-auto pt-8 border-t border-border/60">
            <Button variant="outline" size="icon" asChild className="rounded-full w-12 h-12 border-border/60 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
              <Link to={founder.linkedin} newTab aria-label={`${founder.name} LinkedIn profile`}>
                <Linkedin className="w-5 h-5" />
              </Link>
            </Button>
            <Button variant="outline" size="icon" asChild className="rounded-full w-12 h-12 border-border/60 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors">
              
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}
export default function MindsBehindAizahcare() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="mindsbehindaizahcare" className="bg-background py-40 font-sans relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-secondary/5 blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-24 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 tracking-tight">
            The Team Behind the Project
          </h2>
          <p className="text-lg md:text-xl text-foreground/70 leading-relaxed">
            Led by visionaries in medicine and artificial intelligence, our leadership team is dedicated to building the future of intelligent healthcare infrastructure.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {founders.map((founder, i) => <FounderCard key={founder.id} founder={founder} index={i} />)}
        </div>
      </div>
    </section>;
}